/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

/**
 * Privacy & Film Industry Compliance Utilities
 *
 * Critical for unreleased content protection.
 * These utilities ensure we never accidentally log sensitive information
 * that could reveal unannounced films, plot details, or production secrets.
 */

/**
 * Sensitive field names that should NEVER be captured in analytics
 * These fields may contain:
 * - Unannounced film titles
 * - Scene/shot details
 * - Source footage identifiers
 * - Spoilers/plot points
 * - Production discussions
 */
const SENSITIVE_FIELDS = [
  // Project identifiers
  'project_name',
  'project.name',

  // Clip identifiers
  'clip_name',
  'clip.name',
  'clip.clip_name',
  'scene_name',
  'tape_id',

  // File identifiers
  'file_name',
  'file.name',
  'filename',
  'source_file',

  // VFX identifiers
  'shot_name',
  'vfx_shot_name',

  // Tags (may contain spoilers)
  'tag_name',
  'tag.name',
  'tags.name',

  // User-generated content
  'comment',
  'comment_content',
  'comments.content',
  'notes',
  'description',

  // User PII
  'email',
  'full_name',
  'name',
  'phone',
  'address',
] as const

/**
 * Safe fields that ARE allowed in analytics
 * These are meaningless outside your database
 */
const SAFE_FIELDS = [
  // UUIDs
  'project_id',
  'clip_id',
  'vfx_shot_id',
  'user_id',
  'tag_id',
  'comment_id',
  'commit_id',

  // Counts and metrics
  'count',
  'total',
  'entity_count',
  'query_length',
  'usage_count',

  // Action types
  'action',
  'action_type',
  'event_type',
  'status',

  // NLE/Editor types
  'nle_type',
  'import_type',

  // Feature flags
  'feature',
  'has_field_filter',
  'has_mentions',
  'has_notes',
  'has_avatar',

  // Colors and visual properties
  'color',
  'theme',

  // Timestamps
  'timestamp',
  'created_at',
  'updated_at',

  // Navigation
  'source',
  'destination',
  'location',
  'navigation_item',
] as const

/**
 * Sanitize event properties by removing sensitive fields
 *
 * @param properties - Raw event properties
 * @returns Sanitized properties safe for analytics
 *
 * @example
 * ```ts
 * const raw = {
 *   project_id: 'uuid-123',
 *   project_name: 'Avengers 5', // SENSITIVE!
 *   clip_count: 42,
 * }
 *
 * const safe = sanitizeEventProperties(raw)
 * // { project_id: 'uuid-123', clip_count: 42 }
 * ```
 */
export function sanitizeEventProperties(properties: Record<string, any>): Record<string, any> {
  const sanitized: Record<string, any> = {}

  for (const [key, value] of Object.entries(properties)) {
    // Skip sensitive fields
    if (SENSITIVE_FIELDS.includes(key as any)) {
      console.warn(`[Analytics Privacy] Blocked sensitive field: ${key}`)
      continue
    }

    // Recursively sanitize nested objects
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      sanitized[key] = sanitizeEventProperties(value)
    } else {
      sanitized[key] = value
    }
  }

  return sanitized
}

/**
 * Validate that event properties only contain safe fields
 * Logs errors in development if sensitive fields are found (does NOT throw)
 *
 * @param properties - Event properties to validate
 *
 * @example
 * ```ts
 * validateEventProperties({
 *   project_id: 'uuid-123',
 *   project_name: 'Secret Film', // Will log error in dev!
 * })
 * ```
 */
export function validateEventProperties(properties: Record<string, any>): void {
  // Only validate in development
  if (import.meta.env.DEV) {
    for (const key of Object.keys(properties)) {
      if (SENSITIVE_FIELDS.includes(key as any)) {
        console.error(
          `[Analytics Privacy Violation] Attempted to track sensitive field: "${key}"\n\n` +
          `This field may contain unreleased film content or PII.\n` +
          `The field will be automatically removed, but you should update your tracking call.\n\n` +
          `Location: Check the stack trace above\n` +
          `Field: ${key}\n` +
          `Value: ${JSON.stringify(properties[key])}\n\n` +
          `All sensitive fields: ${SENSITIVE_FIELDS.join(', ')}`
        )
      }
    }
  }
}

/**
 * Get list of all sensitive field names
 * Useful for documentation and debugging
 */
export function getSensitiveFields(): readonly string[] {
  return SENSITIVE_FIELDS
}

/**
 * Get list of all safe field names
 * Useful for documentation and debugging
 */
export function getSafeFields(): readonly string[] {
  return SAFE_FIELDS
}

/**
 * Check if a field name is sensitive
 *
 * @param fieldName - Field name to check
 * @returns true if field is sensitive
 */
export function isSensitiveField(fieldName: string): boolean {
  return SENSITIVE_FIELDS.includes(fieldName as any)
}

/**
 * Check if a field name is explicitly safe
 *
 * @param fieldName - Field name to check
 * @returns true if field is in the safe list
 */
export function isSafeField(fieldName: string): boolean {
  return SAFE_FIELDS.includes(fieldName as any)
}
